/**
 * Product Slider Custom Element
 * Multi-item slider with physics-based drag, momentum, and inertia.
 * Add-to-cart handled by theme's built-in product-form element.
 */

class ProductSlider extends HTMLElement {
  constructor() {
    super();

    // State
    this.isDragging = false;
    this.wasDragged = false;
    this.currentTranslate = 0;
    this.prevTranslate = 0;
    this.currentIndex = 0;
    this.animationID = 0;
    this.autoplayInterval = null;

    // Drag tracking
    this.startX = 0;
    this.startTime = 0;
    this.lastX = 0;
    this.lastTime = 0;
    this.velocityX = 0;

    // Bindings
    this.onDragStart = this.onDragStart.bind(this);
    this.onDragMove = this.onDragMove.bind(this);
    this.onDragEnd = this.onDragEnd.bind(this);
    this.onResize = this.debounce(this.onResize.bind(this), 200);
    this.animate = this.animate.bind(this);
  }

  connectedCallback() {
    this.track = this.querySelector('.product-slider__track');
    this.items = Array.from(this.querySelectorAll('.product-slider__item'));
    
    // Arrows are OUTSIDE the <product-slider> element, in the wrapper
    const wrapper = this.closest('.product-slider__wrapper');
    if (wrapper) {
      this.prevBtn = wrapper.querySelector('.product-slider__arrow--prev');
      this.nextBtn = wrapper.querySelector('.product-slider__arrow--next');
    }
    
    // Dots are OUTSIDE the <product-slider> element too
    const section = this.closest('.product-slider-section');
    if (section) {
      this.dotsContainer = section.querySelector('.product-slider__dots');
    }

    if (!this.track || this.items.length === 0) return;

    // Config from data attributes
    this.autoplayEnabled = this.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(this.dataset.speed, 10) * 1000 || 5000;
    this.pauseOnHover = this.dataset.pauseOnHover === 'true';
    this.infinite = this.dataset.infinite === 'true';
    this.desktopItems = parseFloat(this.dataset.desktopItems) || 4;
    this.mobileItems = parseFloat(this.dataset.mobileItems) || 1.5;

    this.init();
  }

  init() {
    this.calculateLayout();
    this.bindEvents();
    this.createDots();

    if (this.autoplayEnabled) this.startAutoplay();

    // Pause when out of viewport
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) {
          this.stopAutoplay();
        } else if (this.autoplayEnabled) {
          this.startAutoplay();
        }
      });
    }, { threshold: 0.1 });
    this.observer.observe(this);
  }

  // ======== Layout ========

  calculateLayout() {
    const isDesktop = window.matchMedia('(min-width: 750px)').matches;
    this.itemsPerView = isDesktop ? this.desktopItems : this.mobileItems;

    // CSS variables for responsive widths
    this.style.setProperty('--product-slider-desktop-items', this.desktopItems);
    this.style.setProperty('--product-slider-mobile-items', this.mobileItems);

    this.containerWidth = this.clientWidth;
    this.slideWidth = this.containerWidth / this.itemsPerView;
    this.maxIndex = Math.max(0, this.items.length - Math.floor(this.itemsPerView));
    this.maxTranslate = 0;
    this.minTranslate = -(this.items.length * this.slideWidth - this.containerWidth);

    // Clamp
    if (this.currentIndex > this.maxIndex) this.currentIndex = this.maxIndex;
    this.snapToIndex(false);
  }

  onResize() {
    this.calculateLayout();
    this.createDots();
  }

  // ======== Events ========

  bindEvents() {
    // Touch
    this.track.addEventListener('touchstart', this.onDragStart, { passive: true });
    window.addEventListener('touchmove', this.onDragMove, { passive: false });
    window.addEventListener('touchend', this.onDragEnd);

    // Mouse
    this.track.addEventListener('mousedown', this.onDragStart);
    window.addEventListener('mousemove', this.onDragMove);
    window.addEventListener('mouseup', this.onDragEnd);

    // Arrows
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', (e) => { e.preventDefault(); this.prev(); });
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', (e) => { e.preventDefault(); this.next(); });
    }

    // Hover pause
    if (this.pauseOnHover) {
      this.addEventListener('mouseenter', () => this.stopAutoplay());
      this.addEventListener('mouseleave', () => {
        if (this.autoplayEnabled) this.startAutoplay();
      });
    }

    // Prevent context menu
    this.track.addEventListener('contextmenu', e => e.preventDefault());

    // Prevent link clicks during drag (capture phase)
    this.addEventListener('click', (e) => {
      if (this.wasDragged) {
        e.preventDefault();
        e.stopPropagation();
        this.wasDragged = false;
      }
    }, true);

    // Resize
    window.addEventListener('resize', this.onResize);
  }

  // ======== Drag Engine (Physics-based) ========

  getX(e) {
    return e.type.includes('mouse') ? e.pageX : e.touches[0].pageX;
  }

  onDragStart(e) {
    // Don't drag if clicking on a button (Add to Cart, etc.)
    if (e.target.closest('button, .quick-add, product-form, form')) return;

    this.stopAutoplay();
    this.isDragging = true;
    this.wasDragged = false;

    const x = this.getX(e);
    this.startX = x;
    this.lastX = x;
    this.startTime = Date.now();
    this.lastTime = this.startTime;
    this.velocityX = 0;

    this.classList.add('grabbing');
    this.track.classList.remove('product-slider__track--transition');

    cancelAnimationFrame(this.animationID);
    this.animationID = requestAnimationFrame(this.animate);
  }

  onDragMove(e) {
    if (!this.isDragging) return;

    const x = this.getX(e);
    const diff = x - this.startX;
    const now = Date.now();

    // Calculate velocity (exponential moving average for smoothness)
    const dt = now - this.lastTime;
    if (dt > 0) {
      const instantVelocity = (x - this.lastX) / dt;
      this.velocityX = 0.7 * instantVelocity + 0.3 * this.velocityX;
    }

    this.lastX = x;
    this.lastTime = now;

    // Mark as dragged if moved more than 5px
    if (Math.abs(diff) > 5) {
      this.wasDragged = true;
    }

    // Calculate position with elastic resistance at edges
    let newTranslate = this.prevTranslate + diff;

    // Rubber-band / elastic effect at boundaries
    if (newTranslate > this.maxTranslate) {
      const overscroll = newTranslate - this.maxTranslate;
      newTranslate = this.maxTranslate + overscroll * 0.3;
    } else if (newTranslate < this.minTranslate) {
      const overscroll = this.minTranslate - newTranslate;
      newTranslate = this.minTranslate - overscroll * 0.3;
    }

    this.currentTranslate = newTranslate;
  }

  onDragEnd() {
    if (!this.isDragging) return;
    this.isDragging = false;
    cancelAnimationFrame(this.animationID);
    this.classList.remove('grabbing');

    const movedBy = this.currentTranslate - this.prevTranslate;
    const velocity = this.velocityX; // px/ms
    const flickThreshold = 0.15; // px/ms — lower = more sensitive

    // Determine target index based on velocity + distance
    let targetIndex = this.currentIndex;

    if (Math.abs(velocity) > flickThreshold) {
      // Flick detected — use velocity direction
      const momentum = velocity * 150; // project distance
      const projectedTranslate = this.currentTranslate + momentum;
      targetIndex = Math.round(-projectedTranslate / this.slideWidth);
    } else if (Math.abs(movedBy) > this.slideWidth * 0.15) {
      // Slow drag — snap based on direction
      if (movedBy < 0) {
        targetIndex = this.currentIndex + 1;
      } else {
        targetIndex = this.currentIndex - 1;
      }
    } else {
      // Snap to nearest
      targetIndex = Math.round(-this.currentTranslate / this.slideWidth);
    }

    // Clamp or loop
    if (targetIndex < 0) {
      targetIndex = this.infinite ? this.maxIndex : 0;
    }
    if (targetIndex > this.maxIndex) {
      targetIndex = this.infinite ? 0 : this.maxIndex;
    }

    this.currentIndex = targetIndex;
    this.snapToIndex(true);

    if (this.autoplayEnabled) this.startAutoplay();
  }

  animate() {
    this.applyTransform();
    if (this.isDragging) {
      this.animationID = requestAnimationFrame(this.animate);
    }
  }

  applyTransform() {
    this.track.style.transform = `translate3d(${this.currentTranslate}px, 0, 0)`;
  }

  snapToIndex(animate) {
    if (this.currentIndex < 0) this.currentIndex = 0;
    if (this.currentIndex > this.maxIndex) this.currentIndex = this.maxIndex;

    this.currentTranslate = -this.currentIndex * this.slideWidth;

    // Clamp to prevent overscroll
    if (this.currentTranslate > this.maxTranslate) this.currentTranslate = this.maxTranslate;
    if (this.currentTranslate < this.minTranslate) this.currentTranslate = this.minTranslate;

    this.prevTranslate = this.currentTranslate;

    if (animate) {
      this.track.classList.add('product-slider__track--transition');
    }

    this.applyTransform();
    this.updateDots();
  }

  // ======== Navigation ========

  prev() {
    this.stopAutoplay();
    this.currentIndex -= 1;
    if (this.currentIndex < 0) {
      this.currentIndex = this.infinite ? this.maxIndex : 0;
    }
    this.snapToIndex(true);
    if (this.autoplayEnabled) this.startAutoplay();
  }

  next() {
    this.stopAutoplay();
    this.currentIndex += 1;
    if (this.currentIndex > this.maxIndex) {
      this.currentIndex = this.infinite ? 0 : this.maxIndex;
    }
    this.snapToIndex(true);
    if (this.autoplayEnabled) this.startAutoplay();
  }

  // ======== Dots ========

  createDots() {
    if (!this.dotsContainer) return;
    this.dotsContainer.innerHTML = '';
    const dotCount = Math.floor(this.maxIndex) + 1;

    for (let i = 0; i < dotCount; i++) {
      const dot = document.createElement('button');
      dot.classList.add('product-slider__dot');
      dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
      dot.addEventListener('click', () => {
        this.stopAutoplay();
        this.currentIndex = i;
        this.snapToIndex(true);
        if (this.autoplayEnabled) this.startAutoplay();
      });
      this.dotsContainer.appendChild(dot);
    }
    this.dots = Array.from(this.dotsContainer.querySelectorAll('.product-slider__dot'));
    this.updateDots();
  }

  updateDots() {
    if (!this.dots || this.dots.length === 0) return;
    const active = Math.round(this.currentIndex);
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('active', i === active);
    });
  }

  // ======== Autoplay ========

  startAutoplay() {
    this.stopAutoplay();
    this.autoplayInterval = setInterval(() => this.next(), this.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  // ======== Utility ========

  debounce(fn, ms) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), ms);
    };
  }
}

customElements.define('product-slider', ProductSlider);
